import React, {useState} from "react"
import {Button, Form, Input, Select, Switch, Upload, Modal, message, Tooltip} from "antd";
import ImgCrop from "antd-img-crop";
import type {UploadChangeParam} from "antd/es/upload";
import {UploadFile, UploadProps} from "antd/es/upload/interface";

// ----- Local calls -----
import UploadImageButton from "components/UploadImageButton";
import {handleBeforeUpload} from "utils/upload";
import {CampaignInCreate} from "types";
import {FormInstance} from "antd/es/form/hooks/useForm";
import {CampaignsService} from "services";
import {AxiosError} from "axios";

// ----- Variables -----
const {TextArea} = Input;
const formLayout = {
    labelCol: {span: 6},
    wrapperCol: {span: 8},
};

// ----- Types -----
interface NotificationStepProps {
    form: FormInstance
    campaign: CampaignInCreate | null
    loading: boolean
    setCampaign: (campaign: any) => void
    handleNext?: () => void
    handlePrev?: () => void
    currentStep?: number
    handleSubmit?: () => Promise<any>
}

interface TestNotificationModalProps {
    open: boolean;
    onCancel: () => void;
    campaign: CampaignInCreate | null;
    parentForm: FormInstance;
    imageBase64: string | null;
}

// ----- Components -----
const TestNotificationModal: React.FC<TestNotificationModalProps> = ({ open, onCancel, campaign, parentForm, imageBase64 }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleSendNotification = async (values: { userId: string }) => {
        setLoading(true);
        try {
            const title = parentForm.getFieldValue('title') || '';
            const content = parentForm.getFieldValue('content') || '';
            const url = parentForm.getFieldValue('url') || '';
            const urlType = parentForm.getFieldValue('urlType') || '';
            const silent = !!parentForm.getFieldValue('silent');
            const icon = imageBase64 || campaign?.icon || null;

            const payload = {
                notification: {
                    title,
                    content,
                    icon,
                    url,
                    urlType,
                    user_id: Number(values.userId),
                    silent
                }
            };

            await CampaignsService.sendTestPushNotification(payload);
            message.success('Test notification sent successfully!');
            form.resetFields();
            onCancel();
        } catch (error) {
            const err = error as AxiosError<{ errors?: string[]; message?: string }>;
            const status = err.response?.status;
            const data = err.response?.data;

            if (status === 400 && data && Array.isArray(data.errors) && data.errors.length > 0) {
                message.error(data.errors[0]);
            } else if (status && status >= 500) {
                message.error('Failed to send test notification');
            } else if (data?.message) {
                message.error(data.message);
            } else {
                message.error('Failed to send test notification');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Test Notification"
            open={open}
            onCancel={onCancel}
            footer={null}
            width={500}
        >
            <Form
                form={form}
                onFinish={handleSendNotification}
                layout="vertical"
            >
                <Form.Item
                    name="userId"
                    label="User ID"
                    rules={[{ required: true, message: 'Please enter a user ID!' }]}
                >
                    <Input placeholder="Enter user ID to test notification" />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={loading}
                    >
                        Send Test Notification
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

const NotificationStep: React.FC<NotificationStepProps> = ({form, loading, setCampaign, handleNext, campaign}) => {
    // ----- Form -----
    // const [form] = Form.useForm();

    // ----- Upload image -----
    const [imageBase64, setImageBase64] = useState<string | null>(null);
    const [image, _] = useState<string | null>(null);
    
    // ----- Test notification modal -----
    const [testModalOpen, setTestModalOpen] = useState(false);

    // ----- Handlers -----
    const handleFinish = (values: any) => {
        // console.log(values)
        setCampaign((currentValue: CampaignInCreate | null) => ({
            ...(currentValue ? currentValue : {}),
            ...values,
            silent: !!values?.silent,
            icon: imageBase64
        }))
        // todo: clear image base 64 on success
        handleNext?.()
    }

    const handleImageChange: UploadProps['onChange'] = (e: UploadChangeParam<UploadFile>) => {
        console.log('image changed', e)
    };

    const handleTestNotification = () => {
        setTestModalOpen(true);
    };

    const handleTestModalCancel = () => {
        setTestModalOpen(false);
    };

    // ----- Derived state -----
    const watchedCampaignName = Form.useWatch('campaignName', form);
    const watchedTitle = Form.useWatch('title', form);
    const watchedContent = Form.useWatch('content', form);
    const isTestDisabled = !watchedCampaignName || !watchedTitle || !watchedContent;

    return (
        <>
            <Form
                form={form}
                onFinish={handleFinish}
                {...formLayout}
            >
                <Form.Item
                    name="campaignName"
                    label="Campaign name"
                    rules={[{required: true, message: 'Please input name!'}]}
                >
                    <Input/>
                </Form.Item>
                <Form.Item
                    name="category"
                    label="Notification category"
                    rules={[{required: true, message: 'Please select notification category!'}]}
                >
                    <Select>
                        <Select.Option value="promotional">Promotional</Select.Option>
                        <Select.Option value="pavlok_update">Pavlok Update</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    name="title"
                    label="Title"
                    rules={[{required: true, message: 'Please input the title!'}]}
                >
                    <Input/>
                </Form.Item>
                <Form.Item
                    label="Content"
                    name="content"
                    rules={[{required: true, message: 'Please input the content!'}]}
                >
                    <TextArea/>
                </Form.Item>
                <Form.Item label="Icon" name="icon">
                    <ImgCrop showGrid showReset aspect={1 / 1}>
                        <Upload
                            beforeUpload={handleBeforeUpload({
                                config: {allowOnlyImage: true},
                                callback: setImageBase64
                            })}
                            onChange={handleImageChange}
                            accept="image/*"
                            showUploadList={false}
                            onRemove={() => console.log('Remove')}
                            listType="picture-card"
                            disabled={loading}
                        >
                            {
                                (imageBase64 || image) ?
                                    <img src={imageBase64 || image || ''}
                                         alt="Preview"
                                         style={{maxWidth: "100%", borderRadius: 7}}/> :
                                    <UploadImageButton loading={loading}/>
                            }
                        </Upload>
                    </ImgCrop>
                </Form.Item>
                <Form.Item
                    label="URL"
                    name="url"
                >
                    <Input disabled={form.getFieldValue('disabledCampaignUrl') === 'true'}/>
                </Form.Item>
                <Form.Item
                    label="URL Type"
                    name="urlType"
                >
                    <Select disabled={form.getFieldValue('disabledCampaignUrl') === 'true'}>
                        <Select.Option value="internal">Internal</Select.Option>
                        <Select.Option value="external">External</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item name="silent" label="Silent" valuePropName="checked" initialValue={false}>
                    <Switch onChange={(checked) => setCampaign((currentValue: CampaignInCreate | null) => ({
                        ...(currentValue ? currentValue : {}),
                        silent: checked
                    }))}/>
                </Form.Item>
                <div style={{textAlign: 'right'}}>
                    <Form.Item>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                            <Tooltip title={isTestDisabled ? 'Campaign name, title, and content are required' : ''}>
                                <span>
                                    <Button 
                                        type="default" 
                                        onClick={handleTestNotification}
                                        disabled={isTestDisabled}
                                    >
                                        Test Notification
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button type="primary" htmlType="submit">
                                Next
                            </Button>
                        </div>
                    </Form.Item>
                </div>
            </Form>

            <TestNotificationModal
                open={testModalOpen}
                onCancel={handleTestModalCancel}
                campaign={campaign}
                parentForm={form}
                imageBase64={imageBase64}
            />
        </>
    )
}

export default NotificationStep